import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Grid,
  Paper,
  Typography,
  Stack,
  GlobalStyles,
  Fade,
  Zoom,
  Slide,
} from "@mui/material";
import ColorUtils from "../utils/colors";
import logo256 from "../assets/logo_256.png";
import phonePage from "../assets/PhonePage.png";
import webPage from "../assets/WebPage.png";
import localization from "../utils/localization";
import KeyFeature from "../components/KeyFeature";
import ServerUtils from "../utils/server";
import toast from "react-hot-toast";
import IndexCard from "../components/IndexCard";
import { useNavigate } from "react-router-dom";

export function Index(props) {
  const [pwdList, setPwdList] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollTop > 50);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  function refreshPwdList() {
    ServerUtils.listNewest().then(async (resp) => {
      if (!resp.ok) {
        toast.error(localization.strings.networkErr);
        return;
      }
      let respJson = await resp.json();
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson));
        return;
      }
      setPwdList(respJson.data.slice(0, 3));
    });
  }

  useEffect(() => {
    refreshPwdList();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        backgroundColor: "#F8FAFC",
        width: "100%",
        height: "100%",
        overflowY: "scroll",
        overflowX: "hidden",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <GlobalStyles
        styles={{
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-20px)" },
          },
          "@keyframes float-delay": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-15px)" },
          },
          "@keyframes pulse-subtle": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.8 },
          },
        }}
      />

      {/* Enhanced Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.85)" : "transparent",
          backdropFilter: isScrolled ? "blur(12px)" : "none",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          borderBottom: isScrolled ? "1px solid rgba(0, 0, 0, 0.05)" : "1px solid transparent",
          py: isScrolled ? 1 : 2,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <Box
                sx={{
                  p: "2px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)",
                  boxShadow: "0 8px 16px rgba(30, 136, 229, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={logo256}
                  alt="EasyPass Logo"
                  sx={{
                    display: "block",
                    height: "2.4rem",
                    width: "2.4rem",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    p: "1px",
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: "#1E293B",
                  ml: 2,
                  letterSpacing: "-0.03em",
                  fontSize: "1.5rem",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                EasyPass
              </Typography>
            </Stack>

            <Stack direction="row" spacing={{ xs: 1, sm: 3 }} alignItems="center">
              <Button
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  display: { xs: "none", sm: "block" },
                  "&:hover": { color: "#1E88E5", backgroundColor: "transparent" }
                }}
                onClick={() => {
                  navigate(`/login.html?lang=${localization.language.lang}`);
                }}
              >
                {localization.strings.login}
              </Button>

              <Button
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  fontSize: "0.95rem",
                  backgroundColor: "#1E88E5",
                  boxShadow: "0 4px 12px rgba(30, 136, 229, 0.25)",
                  "&:hover": {
                    backgroundColor: "#1565C0",
                    boxShadow: "0 6px 16px rgba(30, 136, 229, 0.35)",
                    transform: "translateY(-1px)"
                  }
                }}
                onClick={() => {
                  navigate(`/login.html?lang=${localization.language.lang}`);
                }}
              >
                {localization.strings.register}
              </Button>

              <Box sx={{ width: "1px", height: "1.5rem", backgroundColor: "rgba(0,0,0,0.08)", mx: 0.5 }} />

              <FormControl size="small">
                <Select
                  value={localization.language.lang || "en"}
                  onChange={(event) => {
                    window.location.href = "?lang=" + event.target.value;
                  }}
                  sx={{
                    color: "#1E293B",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    backgroundColor: "rgba(0,0,0,0.04)",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    height: "2.4rem",
                  }}
                >
                  <MenuItem value="en">EN</MenuItem>
                  <MenuItem value="zh">ZH</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ position: "relative", pt: { xs: 4, md: 8 }, pb: { xs: 8, md: 12 }, overflow: "hidden" }}>
        {/* Background Decorative Elements */}
        <Box sx={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "40%",
          height: "60%",
          background: "radial-gradient(circle, rgba(30, 136, 229, 0.08) 0%, rgba(30, 136, 229, 0) 70%)",
          zIndex: 0,
          filter: "blur(60px)",
        }} />
        <Box sx={{
          position: "absolute",
          bottom: "10%",
          left: "-5%",
          width: "30%",
          height: "50%",
          background: "radial-gradient(circle, rgba(139, 195, 74, 0.06) 0%, rgba(139, 195, 74, 0) 70%)",
          zIndex: 0,
          filter: "blur(50px)",
        }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#1E88E5",
                      fontWeight: 800,
                      letterSpacing: "0.15em",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    SECURE & SIMPLE
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      color: "#1E293B",
                      fontWeight: 800,
                      lineHeight: 1.1,
                      mb: 3,
                      fontSize: { xs: "2.8rem", md: "4rem" },
                      fontFamily: "'Poppins', sans-serif",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    <Box component="span" sx={{
                      background: "linear-gradient(135deg, #FF5252 0%, #D32F2F 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}>
                      {localization.strings.secure}
                    </Box>{" "}
                    {localization.strings.indexTitle}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#64748B",
                      fontSize: "1.2rem",
                      lineHeight: 1.7,
                      mb: 5,
                      maxWidth: "540px",
                    }}
                  >
                    {localization.strings.indexTitleDesc}
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    {[
                      { label: "AppStore", icon: "icon-ios", color: "#000000", href: "https://apps.apple.com/cn/app/easypass%E5%AF%86%E7%A0%81%E7%AE%A1%E7%90%86%E5%B7%A5%E5%85%B7/id1534324173" },
                      { label: "GooglePlay", icon: "icon-google-play", color: "#4285F4", href: "https://play.google.com/store/apps/details?id=com.musketeer.easypass" },
                      { label: "Android", icon: "icon-android-fill", color: "#3DDC84", href: "https://easypass-tech.oss-cn-hangzhou.aliyuncs.com/app-release.apk" },
                      { label: localization.strings.extension, icon: "icon-extension", color: "#FF9800", href: "https://chromewebstore.google.com/detail/easypass/hepcelaldempfnmhkdehiggikjdkiahh" },
                      { label: "Web UI", icon: "icon-chrome", color: "#1E88E5", href: `/login.html?lang=${localization.language.lang}` },
                    ].map((item, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        href={item.href}
                        target="_blank"
                        startIcon={<i className={`iconfont ${item.icon}`} style={{ fontSize: "1.2rem" }} />}
                        sx={{
                          borderRadius: "14px",
                          px: 2.5,
                          py: 1.2,
                          textTransform: "none",
                          fontWeight: 700,
                          borderColor: "rgba(0,0,0,0.1)",
                          color: "#1E293B",
                          backgroundColor: "#fff",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            borderColor: item.color,
                            backgroundColor: `${item.color}05`,
                            transform: "translateY(-4px)",
                            boxShadow: `0 8px 20px ${item.color}15`,
                            color: item.color,
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              </Fade>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                position: "relative",
                display: { xs: "none", md: "block" },
              }}
            >
              <Box sx={{ position: "relative", height: "40rem" }}>
                <Zoom in={true} style={{ transitionDelay: '300ms' }}>
                  <Box
                    component="img"
                    src={webPage}
                    sx={{
                      position: "absolute",
                      top: "2rem",
                      right: "0",
                      width: "100%",
                      maxWidth: "36rem",
                      borderRadius: "12px",
                      boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
                      zIndex: 1,
                      animation: "float-delay 8s ease-in-out infinite",
                    }}
                  />
                </Zoom>
                <Zoom in={true} style={{ transitionDelay: '600ms' }}>
                  <Box
                    component="img"
                    src={phonePage}
                    sx={{
                      position: "absolute",
                      bottom: "2rem",
                      left: "-2rem",
                      height: "32rem",
                      zIndex: 2,
                      filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.15))",
                      animation: "float 6s ease-in-out infinite",
                    }}
                  />
                </Zoom>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 12, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                color: "#1E293B",
                fontWeight: 800,
                mb: 2,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {localization.strings.indexSection1Title}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ color: "#64748B", fontSize: "1.1rem", maxWidth: "600px", mx: "auto" }}
            >
              {localization.strings.indexSection1SubTitle}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              { icon: "icon-check", color: "#4CAF50", bg: "rgba(76, 175, 80, 0.1)", title: localization.strings.indexKeyFeature1Title, desc: localization.strings.indexKeyFeature1Desc },
              { icon: "icon-cloud", color: "#2196F3", bg: "rgba(33, 150, 243, 0.1)", title: localization.strings.indexKeyFeature2Title, desc: localization.strings.indexKeyFeature2Desc },
              { icon: "icon-lock", color: "#E91E63", bg: "rgba(233, 30, 99, 0.1)", title: localization.strings.indexKeyFeature3Title, desc: localization.strings.indexKeyFeature3Desc },
              { icon: "icon-bitcoin", color: "#FF9800", bg: "rgba(255, 152, 0, 0.1)", title: localization.strings.indexKeyFeature4Title, desc: localization.strings.indexKeyFeature4Desc },
              { icon: "icon-envelope", color: "#673AB7", bg: "rgba(103, 58, 183, 0.1)", title: localization.strings.indexKeyFeature5Title, desc: localization.strings.indexKeyFeature5Desc },
            ].map((feature, idx) => (
              <Grid item xs={12} sm={idx < 3 ? 4 : 6} key={idx}>
                <KeyFeature
                  icon={feature.icon}
                  iconColor={feature.color}
                  iconColorBg={feature.bg}
                  title={feature.title}
                  desc={feature.desc}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Real-time Demo Section */}
      {/* <Box sx={{ backgroundColor: "#F8FAFC", py: 12, borderTop: "1px solid rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.03)" }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="flex-end" sx={{ mb: 6 }}>
            <Box>
              <Typography
                variant="h3"
                sx={{ color: "#1E293B", fontWeight: 800, mb: 2, fontFamily: "'Poppins', sans-serif" }}
              >
                {localization.strings.indexSection2Title}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: "#64748B", fontSize: "1.1rem" }}
              >
                {localization.strings.indexSection2SubTitle}
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={refreshPwdList}
              startIcon={<i className="iconfont icon-refresh" />}
              sx={{
                mt: { xs: 2, md: 0 },
                color: "#1E88E5",
                fontWeight: 700,
                "&:hover": { backgroundColor: "rgba(30, 136, 229, 0.05)" }
              }}
            >
              {localization.strings.refresh}
            </Button>
          </Stack>

          <Grid container spacing={4}>
            {pwdList.length > 0 ? (
              pwdList.map((item, index) => (
                <Grid key={index} item xs={12} md={4}>
                  <IndexCard pwd={item} />
                </Grid>
              ))
            ) : (
              [1, 2, 3].map((_, i) => (
                <Grid key={i} item xs={12} md={4}>
                  <Box sx={{ height: 200, borderRadius: "20px", backgroundColor: "rgba(0,0,0,0.03)", animation: "pulse-subtle 2s infinite" }} />
                </Grid>
              ))
            )}
          </Grid>
        </Container>
      </Box> */}

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: 15 }}>
        <Box sx={{ textAlign: "center", mb: 10 }}>
          <Typography
            variant="h3"
            sx={{ color: "#1E293B", fontWeight: 800, mb: 2, fontFamily: "'Poppins', sans-serif" }}
          >
            {localization.strings.pricing}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "#64748B", fontSize: "1.1rem" }}
          >
            Choose the plan that fits your security needs
          </Typography>
        </Box>

        <Grid container spacing={5} justifyContent="center">
          {[
            {
              title: localization.strings.freeUser,
              price: "$0",
              period: "",
              features: [
                { text: localization.strings.editPassword, ok: true },
                { text: localization.strings.editText, ok: true },
                { text: localization.strings.editTotp, ok: true },
                { text: localization.strings.editBlockchain, ok: true },
                { text: localization.strings.editMessage, ok: true },
                { text: localization.strings.editTag, ok: false },
                { text: localization.strings.addExtraKeys, ok: false },
                { text: localization.strings.autocomplete, ok: false },
              ],
              buttonText: localization.strings.newUserVipTime,
              highlight: false,
            },
            {
              title: localization.strings.vipUser,
              price: localization.strings.vipPayInfo,
              period: "",
              features: [
                { text: localization.strings.editPassword, ok: true },
                { text: localization.strings.editText, ok: true },
                { text: localization.strings.editTotp, ok: true },
                { text: localization.strings.editBlockchain, ok: true },
                { text: localization.strings.editMessage, ok: true },
                { text: localization.strings.editTag, ok: true },
                { text: localization.strings.addExtraKeys, ok: true },
                { text: localization.strings.autocomplete, ok: true },
              ],
              buttonText: localization.strings.goPay,
              highlight: true,
            },
          ].map((plan, idx) => (
            <Grid item key={idx} xs={12} sm={6} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "32px",
                  position: "relative",
                  backgroundColor: plan.highlight ? "#1E293B" : "#fff",
                  color: plan.highlight ? "#fff" : "#1E293B",
                  border: plan.highlight ? "none" : "1px solid rgba(0,0,0,0.06)",
                  boxShadow: plan.highlight
                    ? "0 20px 40px rgba(15, 23, 42, 0.2)"
                    : "0 10px 30px rgba(0,0,0,0.04)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: plan.highlight
                      ? "0 30px 60px rgba(15, 23, 42, 0.3)"
                      : "0 20px 50px rgba(0,0,0,0.08)",
                  },
                }}
              >
                {plan.highlight && (
                  <Box sx={{
                    position: "absolute",
                    top: 24,
                    right: 24,
                    backgroundColor: "#1E88E5",
                    color: "#fff",
                    px: 2,
                    py: 0.5,
                    borderRadius: "20px",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                  }}>
                    POPULAR
                  </Box>
                )}
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 800, fontFamily: "'Poppins', sans-serif" }}>
                  {plan.title}
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 4 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: "'Poppins', sans-serif" }}>
                    {plan.price}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {plan.period}
                  </Typography>
                </Stack>

                <Stack spacing={2} sx={{ mb: 6, flexGrow: 1 }}>
                  {plan.features.map((feat, fIdx) => (
                    <Stack key={fIdx} direction="row" alignItems="center" spacing={2}>
                      <Box sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: feat.ok ? (plan.highlight ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.1)") : "rgba(0,0,0,0.05)",
                      }}>
                        <i
                          className={`iconfont ${feat.ok ? "icon-check" : "icon-close"}`}
                          style={{
                            fontSize: "0.75rem",
                            color: feat.ok ? "#4CAF50" : (plan.highlight ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)"),
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, opacity: feat.ok ? 1 : 0.5 }}>
                        {feat.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: "16px",
                    py: 1.8,
                    fontSize: "1rem",
                    fontWeight: 700,
                    backgroundColor: plan.highlight ? "#1E88E5" : "#F1F5F9",
                    color: plan.highlight ? "#fff" : "#1E293B",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: plan.highlight ? "#1565C0" : "#E2E8F0",
                      boxShadow: plan.highlight ? "0 8px 20px rgba(30, 136, 229, 0.4)" : "none",
                    },
                  }}
                >
                  {plan.buttonText}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ backgroundColor: "#1E293B", color: "#F8FAFC", py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src={logo256}
                  sx={{ height: "2.5rem", width: "2.5rem", borderRadius: "10px", mr: 2, filter: "brightness(1.1)" }}
                />
                <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "'Poppins', sans-serif" }}>
                  EasyPass
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "#94A3B8", lineHeight: 1.8, mb: 4 }}>
                Securely manage your passwords, digital wallets, and sensitive information with military-grade encryption that stays on your device.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 3, color: "#fff" }}>PRODUCT</Typography>
              <Stack spacing={2}>
                {["Features", "Pricing", "Download"].map(item => (
                  <Typography key={item} variant="body2" sx={{ color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#fff" } }}>{item}</Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 3, color: "#fff" }}>SUPPORT</Typography>
              <Stack spacing={2}>
                <Typography
                  variant="body2"
                  sx={{ color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#fff" } }}
                  onClick={() => navigate(`/privacy.html?lang=${localization.language.lang}`)}
                >
                  {localization.strings.privacy}
                </Typography>
                <Typography variant="body2" sx={{ color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#fff" } }}>Help Center</Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#fff" } }}
                  onClick={() => navigate(`/refund.html?lang=${localization.language.lang}`)}
                >
                  {localization.strings.refundPolicy}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#fff" } }}
                  onClick={() => navigate(`/terms.html?lang=${localization.language.lang}`)}
                >
                  {localization.strings.termsOfService}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 3, color: "#fff" }}>CONNECT</Typography>
              <Typography variant="body2" sx={{ color: "#94A3B8", mb: 2 }}>Contact us for any inquiries or feedback.</Typography>
              <a
                href="mailto:easypass.inc@gmail.com"
                style={{
                  color: "#fff",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                }}
              >
                <i className="iconfont icon-envelope" style={{ marginRight: "0.75rem", fontSize: "1.2rem" }} />
                easypass.inc@gmail.com
              </a>
            </Grid>
          </Grid>

          <Box sx={{ pt: 6, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              spacing={3}
            >
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                Copyright © 2020-2026 EasyPass. All rights reserved.
              </Typography>
              <Button
                href="https://beian.miit.gov.cn/"
                target="_blank"
                sx={{
                  textTransform: "none",
                  color: "#64748B",
                  fontSize: "0.85rem",
                  "&:hover": { color: "#94A3B8", backgroundColor: "transparent" }
                }}
              >
                浙ICP备18002047号-2
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Index;
